import { useLocalStorage } from '@uidotdev/usehooks';
import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  customerId: string | null;
  expiration: Date | null;
  isAuthenticated : boolean | null;
  permissions: number;   
  canRead:  (permObj: { READ: number })  => boolean;
  canWrite: (permObj: { WRITE: number }) => boolean;
  login: (username: string, password : string) => Promise<boolean>;
  logout : () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

interface AuthResponse {
    access_token : string;
    expire : string
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [customerId, setCustomerId] = useLocalStorage('customer_id', "");
  const [expiration, setExpiration] = useLocalStorage('exp', new Date());
  const [permissions, setPermissions] = useState(0);
  const navigate = useNavigate()

  const isAuthenticated = !!customerId && expiration && new Date() < new Date(expiration);

  const fetchPerms = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/perms`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Could not fetch perms');
      const { perms } = await res.json();     // backend still returns { perms: "100000" }
      // parse as octal just like your menu module
      console.log("Fetched perms:", perms);
      setPermissions(perms);
    } catch (err) {
      console.error('Failed to load permissions', err);
      setPermissions(0);
    }
  };

  // In AuthContext.tsx (or wherever you define the login function)
  const login = async (customerId: string, password: string): Promise<boolean> => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username: customerId, password: password }),
  });

  if (!response.ok) {
    // Parse the error details returned by your backend (e.g., "Invalid Authentication" or "Email not verified...")
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Login failed');
  }

  const data = await response.json() as AuthResponse;
  const expDate = new Date(data.expire).toUTCString();
  // Optionally, you can let the server set the cookie.
  // document.cookie = `session_token=${data.access_token}; path=/; expires=${expDate}`;
  setCustomerId(customerId);
  setExpiration(new Date(data.expire));
  await fetchPerms();  
  return true;
};

  

  const logout = async () => {
  await fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include'
  });
  // now clear your UI state
  setCustomerId('');
  setExpiration(new Date());
  setPermissions(0);   
  navigate('/login');
};
  

  const getCookie = (name : string) => {
    const cookies = document.cookie.split('; ')
    for (let cookie of cookies) {
      const [key, value] = cookie.split('=');
      if (key === name) return value;
    }
    return null;
  }

 useEffect(() => {
    (async () => {
      const token = document.cookie.split('; ').find(c => c.startsWith('session_token='))?.split('=')[1];
      if (!token) return;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/session`, {
        method: 'GET', credentials: 'include'
      });
      if (!res.ok) {
        logout();
        return;
      }
      const { customerId: cid, expiration: expSec } = await res.json() as { customerId: string; expiration: number };
      setCustomerId(cid);
      setExpiration(new Date(expSec * 1000));
      await fetchPerms();              // ← load perms on restore
      const last = localStorage.getItem('lastVisitedPath') || '/';
      localStorage.removeItem('lastVisitedPath');
      navigate(last, { replace: true });
    })();
  }, []);
  
  useEffect(() => {
    if (!isAuthenticated) {
      setPermissions(0);
      const publicPaths = ['/login', '/sign-up'];
      if (!publicPaths.includes(window.location.pathname)) navigate('/login');
    }
    else {
      fetchPerms();
    }
  }, [isAuthenticated, navigate]);
  const canRead  = (p: { READ: number })  => Boolean(permissions & p.READ);
  const canWrite = (p: { WRITE: number }) => Boolean(permissions & p.WRITE);
  return (
    <AuthContext.Provider value={{ customerId, expiration, isAuthenticated, permissions,canRead, canWrite, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};